import React from 'react';
import { useAct, gsap, prefersReduced } from '../hooks/useScrollStory';
import { DOWNLOAD_URL } from '../data/story';
import { Mark } from './Header';

/* ══════════════════════════════════════════════════════
   KAPANIŞ — CTA + HAKKIMIZDA + İLETİŞİM
   Son çağrı, marka cümlesi ve iletişim bilgileri.
   ══════════════════════════════════════════════════════ */

const phoneRaw = '+905436377583';

export default function About() {
  const ref = useAct((root) => {
    const q = gsap.utils.selector(root);
    if (prefersReduced()) return;
    gsap.from(q('.ab-reveal'), {
      opacity: 0, y: 34, ease: 'power3.out', duration: 0.7, stagger: 0.1,
      scrollTrigger: { trigger: root, start: 'top 78%' },
    });
  }, []);

  return (
    <footer ref={ref} id="hakkimizda" className="relative overflow-hidden" style={{ background: '#050605' }}>
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true"
        style={{ background: 'radial-gradient(ellipse 70% 60% at 50% 0%, rgba(200,255,34,.10) 0%, rgba(200,255,34,0) 55%)' }} />

      <div className="relative max-w-[1000px] mx-auto px-6 md:px-10 py-[clamp(90px,15vh,170px)]">
        {/* büyük kapanış çağrısı */}
        <div className="ab-reveal text-center">
          <h2 className="font-display font-bold text-white leading-[1.04] tracking-[-0.03em] mx-auto max-w-[16ch]"
            style={{ fontSize: 'clamp(34px,5.2vw,72px)' }}>
            Tarlanı daha yakından tanı
          </h2>
          <p className="mt-6 text-[16px] md:text-[17px] leading-relaxed text-white/60 max-w-[52ch] mx-auto">
            Agren AI ile bitkini analiz et, tarla verilerini incele ve tarımla ilgili sorularını yapay zekâya sor.
          </p>
          <div className="mt-9 flex flex-col items-center gap-4">
            <a href={DOWNLOAD_URL} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full px-8 py-4 text-[16px] font-semibold"
              style={{ background: '#C8FF22', color: '#0A140A', boxShadow: '0 12px 40px rgba(200,255,34,.3)' }}>
              Uygulamayı İndir <span aria-hidden="true">↓</span>
            </a>
            <span className="font-mono text-[12px] tracking-[.08em] text-white/45">Android'de kullanmaya başlayın</span>
          </div>
        </div>

        {/* ayraç */}
        <div className="ab-reveal my-[clamp(56px,9vh,110px)] h-px w-full" style={{ background: 'linear-gradient(90deg,transparent,rgba(255,255,255,.14),transparent)' }} />

        {/* hakkımızda — detaylı */}
        <div className="ab-reveal max-w-[760px]">
          <span className="font-mono text-[11px] tracking-[.2em] uppercase" style={{ color: '#C8FF22' }}>Hakkımızda</span>
          <h3 className="mt-4 font-display font-bold text-white leading-[1.12] tracking-[-0.02em]" style={{ fontSize: 'clamp(24px,2.8vw,36px)' }}>
            Senin yerine ziraat mühendisi değil,<br className="hidden sm:block" /> yanında taşıdığın akıllı asistan
          </h3>
          <div className="mt-6 space-y-4 text-[15.5px] leading-relaxed text-white/62">
            <p>
              Agren AI; çiftçilerin günlük tarım kararlarını daha düzenli, anlaşılır ve veriye dayalı hâle getirmek için
              geliştirilen bir akıllı tarım uygulamasıdır. Bitki analizini, tarla verilerini, uydu görüşünü ve yapay zekâ
              asistanını tek bir deneyimde bir araya getirir.
            </p>
            <p>
              Amacımız teknolojiyi sahaya, çiftçinin cebine taşımak. Fotoğrafla bitki değerlendirmesinden uydu tabanlı
              tarla izlemeye, tarım asistanından günlük görev takibine kadar üretimin her aşamasında yanında.
            </p>
            <p className="text-white/50">
              Agren AI bir ziraat mühendisinin yerini almaz; senin yerine karar vermez. Tarlanı daha erken, daha yakından
              ve daha bilinçli anlamana yardımcı olur. Son karar her zaman senin — saha koşulların ve uzman görüşüyle birlikte.
            </p>
          </div>
          <div className="mt-7 inline-flex items-center gap-3 font-mono text-[12.5px] tracking-[.14em] uppercase" style={{ color: 'rgba(200,255,34,.85)' }}>
            <span>Gör</span><span aria-hidden="true" className="text-white/30">→</span>
            <span>Anla</span><span aria-hidden="true" className="text-white/30">→</span>
            <span>Takip et</span>
          </div>
        </div>

        {/* ne yapar */}
        <div className="ab-reveal mt-[clamp(40px,6vh,72px)] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { t: 'Bitki Analizi', d: 'Fotoğraf üzerinden belirti değerlendirmesi', p: 'M5 21c8 0 14-6 14-14V4h-3C8 4 3 10 3 18v3h2Zm0 0c0-6 3-10 9-13' },
            { t: 'Tarla Verileri', d: 'Parsel bazlı sağlık ve takip', p: 'M4 20V10M10 20V4M16 20v-8M22 20H2' },
            { t: 'Uydu Görüşü', d: 'Zaman içinde değişim karşılaştırması', p: 'M12 3a9 9 0 1 0 9 9M3 12h18M12 3c2.5 2.5 2.5 15 0 18M12 3C9.5 5.5 9.5 18.5 12 21' },
            { t: 'Tarım Asistanı', d: 'Sorularına bağlamlı yanıt', p: 'M4 5h16v11H8l-4 4V5Z' },
          ].map((c) => (
            <div key={c.t} className="rounded-2xl border border-white/10 p-5" style={{ background: 'rgba(255,255,255,.02)' }}>
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl" style={{ background: 'rgba(200,255,34,.1)', color: '#C8FF22' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d={c.p} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </span>
              <div className="mt-4 font-display font-semibold text-white text-[16px]">{c.t}</div>
              <p className="mt-1.5 text-[13.5px] leading-relaxed text-white/50">{c.d}</p>
            </div>
          ))}
        </div>

        {/* iletişim */}
        <div className="ab-reveal mt-[clamp(44px,7vh,80px)]">
          <span className="font-mono text-[11px] tracking-[.2em] uppercase" style={{ color: '#C8FF22' }}>İletişim</span>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a href={`tel:${phoneRaw}`}
              className="group flex items-center gap-4 rounded-2xl border border-white/10 p-5 transition-colors duration-300 hover:border-lime/30"
              style={{ background: 'rgba(255,255,255,.02)' }}>
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl shrink-0" style={{ background: 'rgba(200,255,34,.1)', color: '#C8FF22' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M6.5 3h3l1.5 4-2 1.5a11 11 0 0 0 5 5L15.5 11l4 1.5v3a2 2 0 0 1-2.2 2A16 16 0 0 1 4.5 5.2 2 2 0 0 1 6.5 3Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /></svg>
              </span>
              <div className="min-w-0">
                <div className="text-[12px] text-white/45">Telefon</div>
                <div className="font-display font-semibold text-white text-[18px] group-hover:text-lime transition-colors duration-300">0543 637 7583</div>
              </div>
            </a>

            <a href="mailto:info@agrenai.com"
              className="group flex items-center gap-4 rounded-2xl border border-white/10 p-5 transition-colors duration-300 hover:border-lime/30"
              style={{ background: 'rgba(255,255,255,.02)' }}>
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl shrink-0" style={{ background: 'rgba(200,255,34,.1)', color: '#C8FF22' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="14" rx="2.5" stroke="currentColor" strokeWidth="1.6" /><path d="m4 7 8 6 8-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </span>
              <div className="min-w-0">
                <div className="text-[12px] text-white/45">E-posta</div>
                <div className="font-display font-semibold text-white text-[18px] group-hover:text-lime transition-colors duration-300 break-all">info@agrenai.com</div>
              </div>
            </a>
          </div>
        </div>

        {/* alt satır */}
        <div className="ab-reveal mt-[clamp(48px,8vh,90px)] flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 border-t border-white/10">
          <a href="#top" className="flex items-center gap-2 font-display font-semibold text-white text-[15px]">
            <Mark size={26} /> Agren <span className="opacity-50">AI</span>
          </a>
          <span className="text-[12.5px] text-white/40">© {new Date().getFullYear()} Agren AI · Akıllı tarım cebinde</span>
        </div>
      </div>
    </footer>
  );
}
